/**
 * Custom hook to track selected text color
 */
import { useState, useEffect } from "react";
import { rgbToHex } from "../utils/colorUtils";

export const useSelectedTextColor = ({
  textId,
  editTextBoxContent,
  showEditNote,
  showEditTask,
  isExpanded,
}) => {
  const [selectedColor, setSelectedColor] = useState("#000000");

  useEffect(() => {
    const handleSelectionChange = () => {
      let colorSource = null;

      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        let node = range.startContainer;

        // Traverse up to find the closest <span> with style.color
        while (node && node !== document) {
          if (
            node.nodeType === Node.ELEMENT_NODE &&
            node.tagName === "SPAN" &&
            node.style?.color
          ) {
            colorSource = node;
            break;
          }
          node = node.parentNode;
        }
      }

      // Fallback: determine root div based on current edit context
      if (!colorSource) {
        let divId = `content-div-${textId}`;
        if (isExpanded && !editTextBoxContent) {
          if (showEditNote) divId = `note-div-${textId}`;
          else if (showEditTask) divId = `task-div-${textId}`;
        }
        const rootDiv = document.getElementById(divId);
        if (rootDiv) {
          const allSpans = rootDiv.querySelectorAll("span[style*='color']");
          if (allSpans.length > 0) {
            colorSource = allSpans[allSpans.length - 1];
          } else {
            colorSource = rootDiv;
          }
        }
      }

      if (!colorSource) return;

      const inlineColor = colorSource.style?.color;
      const computedColor = getComputedStyle(colorSource).color;
      const finalColor = inlineColor || computedColor;

      const hex = rgbToHex(finalColor);
      setSelectedColor(hex);
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    handleSelectionChange(); // Initial

    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, [
    textId,
    editTextBoxContent,
    showEditNote,
    showEditTask,
    isExpanded,
    selectedColor,
  ]);

  return selectedColor;
};
